import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { canSeeCustomerMoney } from '@/lib/constants';
import { validateField } from '@/lib/validators';
import FieldError from '@/components/ui/field-error';
import { Plus, Search, Users, Eye, Pencil, AlertCircle, RefreshCw } from 'lucide-react';

import { Screen, ScreenSplit, Pane, CommandBar, StatStrip, DataGrid, FactBand, Fact, CellButton, SidePane } from '@/components/data';

/**
 * Customers, with what they owe on the same screen.
 *
 * Three of six columns were hidden below `lg` — orders, total value, and
 * outstanding, which is to say all the money. At 30px rows they all fit, and
 * selecting a customer fills the rail with their balance and recent orders, so
 * "what do they owe" stops being a navigation to Customer Profile.
 *
 * Add and Edit share the side pane, which means creating a customer no longer
 * covers the list you were checking them against.
 */

const VALIDATORS = {
  display_name: [{ name: 'required', options: { message: 'Firm name is required' } }],
  gstin: [{ name: 'gstin' }],
  mobile_number: [{ name: 'mobile', options: { allowEmpty: true } }],
  email: [{ name: 'email' }],
};

const EMPTY = {
  display_name: '', mobile_number: '', email: '',
  billing_address: '', delivery_address: '', gstin: '', notes: '',
};

const PAGE_SIZE = 50;
// the name as it was typed — records booked before it was stored as one value
// are put back together from the two halves it was split into
const typedName = (c) => c.display_name
  || (c.firm_name && c.firm_name !== c.customer_name
    ? `${c.firm_name}, ${c.customer_name}`
    : c.firm_name || c.customer_name || '');
const money = (v) => `₹${(v || 0).toLocaleString('en-IN')}`;

export default function CustomersPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const showFinancials = canSeeCustomerMoney(user);
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [owingOnly, setOwingOnly] = useState(false);

  const [pane, setPane] = useState(null); // 'add' | customer
  const [form, setForm] = useState(EMPTY);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const setField = (name, value) => {
    setForm(p => ({ ...p, [name]: value }));
    if (VALIDATORS[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: validateField(name, value, VALIDATORS[name]) }));
    }
  };

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/customers', { params: { search, page, limit: PAGE_SIZE } });
      setCustomers(res.data.customers || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'Failed to load customers';
      setError(message);
      toast.error('Failed to load customers', { description: message });
    } finally {
      setLoading(false);
    }
  }, [search, page]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const openAdd = () => { setForm(EMPTY); setFieldErrors({}); setPane('add'); };
  const openEdit = (customer) => {
    setForm({
      display_name: typedName(customer),
      mobile_number: customer.mobile_number || '',
      email: customer.email || '',
      billing_address: customer.billing_address || '',
      delivery_address: customer.delivery_address || '',
      gstin: customer.gstin || '',
      notes: customer.notes || '',
    });
    setFieldErrors({});
    setPane(customer);
  };

  const save = async () => {
    const errors = {};
    for (const [name, rules] of Object.entries(VALIDATORS)) {
      const error = validateField(name, form[name], rules);
      if (error) errors[name] = error;
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error('Fix the highlighted fields first');
      return;
    }
    setSaving(true);
    try {
      if (pane === 'add') {
        await api.post('/customers', form);
        toast.success('Customer created');
      } else {
        await api.put(`/customers/${pane.id}`, form);
        toast.success('Customer updated');
      }
      setPane(null);
      setForm(EMPTY);
      fetchCustomers();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = Array.isArray(detail) ? detail.map(d => d.msg).join('; ') : detail || 'Failed to save customer';
      if (err.response?.status === 409) setFieldErrors({ display_name: message });
      toast.error(message);
    }
    setSaving(false);
  };

  const visible = useMemo(
    () => (owingOnly ? customers.filter(c => (c.outstanding || 0) > 0) : customers),
    [customers, owingOnly]
  );

  const [sort, setSort] = useState(null);
  const rows = useMemo(() => {
    if (!sort?.key) return visible;
    return [...visible].sort((a, b) => {
      const av = a[sort.key], bv = b[sort.key];
      const cmp = typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av ?? '').localeCompare(String(bv ?? ''), undefined, { numeric: true });
      return sort.dir === 'desc' ? -cmp : cmp;
    });
  }, [visible, sort]);

  const columns = useMemo(() => ([
    {
      key: 'customer_name',
      header: 'Customer',
      width: 'minmax(12rem,1.4fr)',
      sortable: true,
      render: (c) => (
        <span className="block truncate font-semibold text-brand-blue">{typedName(c)}</span>
      ),
    },
    {
      key: 'contact',
      header: 'Contact',
      width: 'minmax(10rem,1.1fr)',
      render: (c) => (
        <>
          <span className="block truncate text-[12px] text-slate-600">{c.mobile_number || '—'}</span>
          {c.email && <span className="block truncate text-[11px] leading-tight text-slate-400">{c.email}</span>}
        </>
      ),
    },
    {
      key: 'gstin',
      mobile: false,
      header: 'GSTIN',
      width: '10rem',
      render: (c) => <span className="block truncate font-mono text-[11px] text-slate-500">{c.gstin || '—'}</span>,
    },
    {
      key: 'total_orders',
      mobile: false,
      header: 'Orders',
      align: 'right',
      width: '5.5rem',
      sortable: true,
      render: (c) => <span className="font-medium">{c.total_orders || 0}</span>,
      total: (list) => list.reduce((s, c) => s + (c.total_orders || 0), 0),
    },
    showFinancials && {
      key: 'total_value',
      mobile: false,
      header: 'Total value',
      align: 'right',
      width: '8rem',
      sortable: true,
      render: (c) => <span className="text-slate-700">{money(c.total_value)}</span>,
      total: (list) => money(list.reduce((s, c) => s + (c.total_value || 0), 0)),
    },
    showFinancials && {
      key: 'outstanding',
      header: 'Outstanding',
      align: 'right',
      width: '8rem',
      sortable: true,
      render: (c) => (
        <span className={`font-medium ${(c.outstanding || 0) > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
          {money(c.outstanding)}
        </span>
      ),
      total: (list) => money(list.reduce((s, c) => s + (c.outstanding || 0), 0)),
    },
    {
      key: 'actions',
      header: '',
      width: '4.5rem',
      align: 'right',
      render: (c) => (
        <span className="flex items-center justify-end gap-0.5">
          <CellButton icon={Eye} label="Open profile" iconOnly
            onClick={() => navigate(`/customers/${c.id}`)} testId={`view-customer-${c.id}`} />
          <CellButton icon={Pencil} label="Edit customer" iconOnly
            onClick={() => openEdit(c)} testId={`edit-customer-${c.id}`} />
        </span>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ].filter(Boolean)), [navigate, showFinancials]);

  const selected = customers.find(c => c.id === selectedId);
  const owingCount = customers.filter(c => (c.outstanding || 0) > 0).length;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <Screen data-testid="customers-page">
      <CommandBar
        title="Customers"
        subtitle={`${total} on file`}
        actions={[
          { key: 'refresh', label: 'Refresh', icon: RefreshCw, onClick: fetchCustomers, disabled: loading },
          {
            key: 'add', label: 'Add Customer', icon: Plus, tone: 'accent', mnemonic: 'a',
            onClick: openAdd, testId: 'add-customer-btn',
          },
        ]}
      >
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="customer-search"
            className="h-7 pl-8 text-xs"
            placeholder="Name, firm, phone…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
      </CommandBar>

      <StatStrip
        stats={[
          { key: null, label: 'customers', value: total, tone: 'blue' },
          showFinancials && { key: 'owing', label: 'owing', value: owingCount, tone: owingCount ? 'danger' : 'good' },
          showFinancials && {
            key: null,
            label: 'outstanding on this page',
            value: money(customers.reduce((s, c) => s + (c.outstanding || 0), 0)),
            tone: 'danger',
          },
        ].filter(Boolean)}
        activeKey={owingOnly ? 'owing' : null}
        onSelect={(key) => setOwingOnly(key === 'owing')}
      />

      {error ? (
        <div className="flex flex-1 flex-col items-center justify-center rounded-lg border border-red-100 bg-white">
          <AlertCircle className="mb-3 h-10 w-10 text-red-400" />
          <p className="mb-1 font-medium text-red-600">Failed to load customers</p>
          <p className="mb-4 text-sm text-slate-500">{error}</p>
          <Button onClick={fetchCustomers} className="bg-brand-blue text-white hover:bg-brand-blue-dark">
            <RefreshCw className="mr-2 h-4 w-4" /> Try again
          </Button>
        </div>
      ) : (
        <ScreenSplit
          railWidth={selected ? '20rem' : '0rem'}
          rail={selected ? (
            <Pane
              title={selected.firm_name || selected.customer_name}
              aside={selected.customer_name !== selected.firm_name ? selected.customer_name : undefined}
              className="max-h-full"
              actions={
                <button
                  type="button"
                  onClick={() => navigate(`/customers/${selected.id}`)}
                  className="rounded px-1.5 py-0.5 text-[10px] font-normal normal-case tracking-normal text-blue-100 hover:bg-white/20"
                >
                  Full profile
                </button>
              }
            >
              <div className="p-1.5">
                <FactBand min="100%" className="border-0 p-0 shadow-none">
                  <Fact label="Mobile">{selected.mobile_number}</Fact>
                  <Fact label="Email">{selected.email}</Fact>
                  <Fact label="GSTIN">{selected.gstin}</Fact>
                  <Fact label="Orders">{selected.total_orders || 0}</Fact>
                  {showFinancials && <Fact label="Billed">{money(selected.total_value)}</Fact>}
                  {showFinancials && (
                    <Fact label="Outstanding" valueClassName={(selected.outstanding || 0) > 0 ? 'text-red-600 font-medium' : 'text-emerald-600'}>
                      {money(selected.outstanding)}
                    </Fact>
                  )}
                  <Fact label="Deliver to">{selected.delivery_address || selected.billing_address}</Fact>
                </FactBand>
              </div>
              {selected.notes && (
                <div className="border-t border-slate-100 px-2.5 py-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Notes</p>
                  <p className="mt-0.5 text-[12px] leading-snug text-slate-600">{selected.notes}</p>
                </div>
              )}
              <div className="border-t border-slate-100 p-2">
                <Button
                  size="sm"
                  className="w-full bg-brand-blue text-white hover:bg-brand-blue-dark"
                  onClick={() => navigate(`/orders/new?customer=${selected.id}`)}
                >
                  <Plus className="mr-1 h-3.5 w-3.5" /> New order for this customer
                </Button>
              </div>
            </Pane>
          ) : null}
        >
          <Pane
            title="Customers"
            aside={owingOnly ? 'owing only' : `${rows.length} shown`}
            scroll={false}
            bodyClassName="flex flex-col"
            actions={totalPages > 1 && (
              <span className="flex items-center gap-1 text-[10px] font-normal normal-case tracking-normal text-blue-100">
                <button type="button" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                  data-testid="prev-page-btn"
                  className="rounded px-1 hover:bg-white/20 disabled:opacity-30">Prev</button>
                {page} / {totalPages}
                <button type="button" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                  data-testid="next-page-btn"
                  className="rounded px-1 hover:bg-white/20 disabled:opacity-30">Next</button>
              </span>
            )}
          >
            <DataGrid
              data-testid="customers-table"
              columns={columns}
              rows={rows}
              loading={loading}
              getRowId={(c) => c.id}
              selectedId={selectedId}
              onRowClick={(c) => setSelectedId(c.id === selectedId ? null : c.id)}
              sort={sort}
              onSortChange={setSort}
              showTotals={rows.length > 0}
              empty={
                <span className="flex flex-col items-center gap-3 text-slate-400">
                  <Users className="h-8 w-8 text-slate-300" />
                  No customers found
                  <Button onClick={openAdd} className="bg-brand-blue text-white hover:bg-brand-blue-dark"
                    data-testid="empty-add-customer-btn">
                    Add first customer
                  </Button>
                </span>
              }
            />
          </Pane>
        </ScreenSplit>
      )}

      <SidePane
        open={!!pane}
        onClose={() => { setPane(null); setForm(EMPTY); setFieldErrors({}); }}
        title={pane === 'add' ? 'Add customer' : `Edit ${pane ? typedName(pane) : 'customer'}`}
        width="26rem"
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => { setPane(null); setForm(EMPTY); }}>Cancel</Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="save-customer-btn"
              className="bg-brand-blue text-white hover:bg-brand-blue-dark">
              {saving ? 'Saving…' : pane === 'add' ? 'Create customer' : 'Save changes'}
            </Button>
          </>
        }
      >
        <div className="space-y-2.5 p-3">
          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">Firm Name, Customer Name *</span>
            <Input className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" value={form.display_name}
              data-testid="customer-name-input"
              onChange={e => setField('display_name', e.target.value)} />
            <p className="mt-0.5 text-[10px] leading-snug text-slate-400">
              Type the firm name, a comma, then the contact person — e.g. Shreeji Packaging, Ramesh Kumar
            </p>
            <FieldError error={fieldErrors.display_name} />
          </label>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <label className="block">
              <span className="text-[11px] font-medium text-slate-600">Mobile</span>
              <Input className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" value={form.mobile_number}
                placeholder="10-digit number" data-testid="customer-mobile-input"
                onChange={e => setField('mobile_number', e.target.value)} />
              <FieldError error={fieldErrors.mobile_number} />
            </label>
            <label className="block">
              <span className="text-[11px] font-medium text-slate-600">Email</span>
              <Input className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" value={form.email}
                placeholder="example@domain.com" data-testid="customer-email-input"
                onChange={e => setField('email', e.target.value)} />
              <FieldError error={fieldErrors.email} />
            </label>
          </div>

          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">GSTIN</span>
            <Input className="mt-0.5 h-8 font-mono text-sm" value={form.gstin}
              placeholder="15 characters — e.g. 27AABCG1234Q1Z5" data-testid="customer-gstin-input"
              onChange={e => setField('gstin', e.target.value.toUpperCase())} />
            <FieldError error={fieldErrors.gstin} />
          </label>

          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">Billing address</span>
            <Textarea className="mt-0.5 resize-none text-sm" rows={2} value={form.billing_address}
              data-testid="customer-billing-input"
              onChange={e => setForm(p => ({ ...p, billing_address: e.target.value }))} />
          </label>
          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">Delivery address</span>
            <Textarea className="mt-0.5 resize-none text-sm" rows={2} value={form.delivery_address}
              data-testid="customer-delivery-input"
              onChange={e => setForm(p => ({ ...p, delivery_address: e.target.value }))} />
          </label>
          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">Notes</span>
            <Textarea className="mt-0.5 resize-none text-sm" rows={2} value={form.notes}
              data-testid="customer-notes-input"
              onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
          </label>
        </div>
      </SidePane>
    </Screen>
  );
}
